import React, { ChangeEvent, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Grid } from '@mui/material'
import * as R from 'ramda'
import {
  PuiTextField,
  useFields,
  ValidateOptions,
} from '@pbt/pbt-ui-components'

import PuiSwitch from '~/components/common/PuiSwitch'
import { AbaxisLabCredentials, LabProps } from '~/types'
import { usePracticeFieldsSection } from '~/utils/usePracticeFieldsSection'

import { PracticeDetailsPanels } from '../../../practices'
import VendorFormContainer from '../VendorFormContainer'

const AbaxisLab = ({
  business,
  initiallyActive,
  credentials,
  status,
  name,
  isTesting,
  vendorId,
  onTest,
  onToggle,
}: LabProps<AbaxisLabCredentials>) => {
  const { t } = useTranslation('Common')

  const { fields, validate, reset } = useFields(
    [
      {
        name: 'active',
        label: name,
        type: 'toggle',
        initialValue: initiallyActive || false,
      },
      {
        name: 'clientId',
        label: t('Common:CLIENT_ID'),
        validators: ['required'],
        initialValue: credentials.clientId || '',
      },
    ],
    false,
  )

  const { active, clientId } = fields

  usePracticeFieldsSection({
    business,
    fields,
    sectionName:
      `${PracticeDetailsPanels.LAB_INTEGRATIONS}_ABAXIS` as PracticeDetailsPanels,
    parentSectionName: PracticeDetailsPanels.LAB_INTEGRATIONS,
    validate: (options?: ValidateOptions | undefined): boolean => {
      if (active.value) {
        return validate(options)
      }
      return true
    },
    reset,
    resetDependencies: [initiallyActive, business],
  })

  return (
    <>
      <PuiSwitch
        field={{
          ...active,
          set: (event: ChangeEvent<HTMLInputElement>) =>
            onToggle(event.target?.checked, () => active.set(event)),
        }}
        label={active.label}
      />
      {active?.value && (
        <>
          <Grid container pb={3} pl={6}>
            <VendorFormContainer
              isTesting={isTesting}
              status={status}
              onTest={() =>
                onTest({
                  active: active.value,
                  clientId: clientId.value,
                  vendorId,
                })
              }
            >
              <Grid container pr={1}>
                <PuiTextField
                  field={clientId}
                  inputProps={{ maxLength: 100 }}
                  label={clientId.label}
                />
              </Grid>
            </VendorFormContainer>
          </Grid>
          <Divider />
        </>
      )}
    </>
  )
}

export default memo(AbaxisLab, (prevProps, nextProps) =>
  R.equals(prevProps, nextProps),
)
